// @avantia common-code
import { addToWindowRef, ensureDefined, SMap } from '@avantia/client-and-server-utilities';
import { validateFields } from '@avantia/form-validation';
import { lodash } from '@avantia/lodash';
import {
  createControlId,
  HpAddControlGroupActionProps,
  HpAnswerMap,
  HpControlActionTypes,
  HpRemoveControlGroupActionProps
} from '@avantia/questionset-model';
import { v4 as uuid } from 'uuid';
import { HpDynamicButtonClickAction, HpDynamicButtonClickPayload } from '../actions/dynamicButtonClickActionTypes';
import { triggerElementToggledEvent } from '../actions/eventActions';
import { HpFluentAnswerClickAction } from '../actions/fluentAnswerClickActionTypes';
import { HpGetAnswersAction } from '../actions/getAnswersActionTypes';
import { HpGetQuestionSetAction } from '../actions/getQuestionSetActionTypes';
import { HpGetQuestionSetsAction } from '../actions/getQuestionSetsActionTypes';
import { HpHelpClickAction } from '../actions/helpClickActionTypes';
import { HpNavigationChangedAction } from '../actions/navigationChangedActionTypes';
import { HpSetActiveSectionAction } from '../actions/setActiveSectionActionTypes';
import { HpClientDebugFlags } from '../clientDebugFlags';
import { logError, logInfo, logWarning } from '../clientLogging';
import { registerConfigurationAction } from '../config/registerClientConfig';
import { getCustomExtension } from '../customExtensionRegistry';
import {
  HpGetRouteKeyFromSectionIdFunction,
  HpQuestionSetAnswersRetrievedReducerFunction
} from '../customExtensionRegistryTypes';
import { getPerQuestionSetConfig } from '../perQuestionSetConfigRegistry';
import { areSame, HpMetaData, setMetaDataInAnswers } from '../tools/clientAndServerUtilities';
import { getAnswersIdCookie, setAnswersIdCookie, setProductIdCookie } from '../tools/cookieUtilities';
import { HpSetActiveSectionOptions } from '../tools/questionDisplayStateTools';
import { getQuestionIdFor } from '../tools/questionIds';
import {
  HpAction,
  HpClientAvailableQuestionSet,
  HpClientButtonClickActionFunction,
  HpClientButtonData,
  HpClientDependentAction,
  HpClientQuestion,
  HpClientQuestionSetState,
  HpClientValidationResult,
  HpEngineActionTypes,
  HpEventTypes,
  HpFieldChangedAction,
  HpFieldChangedPayload,
  HpQuestionDisplayState,
  HpQuestionScriptAction,
  HpRegisterSomethingFunction,
  HpScriptActionTypes
} from '../types';
import { createEmptyValidationResult, getInitialState } from './initialState';
import { questionSetPayloadMapper } from './questionSetPayloadMapper';
import {
  addControlGroup,
  changeState,
  createValidationModel,
  evaluateCondition,
  fieldChangedHandler,
  getIsValidControlTypeFunction,
  processPrereqsAndCalcs,
  removeControlGroup,
  updateAnswerDependentAnswers,
  updateDateDropDownQuestionUI,
  validateAndMaybeMoveSection
} from './questionSetReducerLibrary';
import { applyChanges } from './reducerLibrary';
import { swapInQuestions, swapOutQuestions } from './reducerOptimiser';
import { ReducerProcessor } from './reducerProcessor';

const processor = new ReducerProcessor<HpClientQuestionSetState, HpEngineActionTypes>('QuestionSet');
const isValidControlType = getIsValidControlTypeFunction();
let unvalidatedNavigationEnabled: boolean;

registerConfigurationAction((config) => {
  const unvalidatedNavEnabled = config.unvalidatedNavigationEnabled;
  processor.setDeveloperModeEnabled(config.environment === 'developer');
  unvalidatedNavigationEnabled = unvalidatedNavEnabled === true;
});

export const questionSetReducerProcessor = processor; // so it can be extended if necessary.

processor.add<HpFieldChangedAction>('FIELD_CHANGED', (state, action) => {
  return fieldChangedHandler({ state, payload: action.payload, isDelayedValidation: false });
});

processor.add<HpFieldChangedAction>('DELAYED_FIELD_VALIDATION', (state, action) => {
  return fieldChangedHandler({ state, payload: action.payload, isDelayedValidation: true });
});

processor.add<HpFieldChangedAction>('FIELD_BLURRED', (state, action) => {
  const { detail } = action.payload as HpFieldChangedPayload;
  if (detail.type === 'date-drop-down') {
    state = updateDateDropDownQuestionUI(detail, state);
  }

  return fieldChangedHandler({ state, payload: action.payload, isDelayedValidation: false });
});

processor.add<HpGetQuestionSetAction>('QUESTIONSET_RETRIEVED', (state, action) => {
  const { payload } = action;
  const { questionSet: qsPayload, keepAnswers } = ensureDefined(payload, 'payload');
  const { answersId = getAnswersIdCookie(), answers: payloadAnswers } = qsPayload;
  const questionSet = questionSetPayloadMapper({
    currState: state,
    payload: { ...qsPayload, answers: keepAnswers ? state.answers : payloadAnswers },
    errorIfNoPayload: false,
    isValidControlType
  }) as HpClientQuestionSetState;

  if (!questionSet.answersId && answersId) {
    questionSet.answersId = answersId;
  }

  questionSet.loadTime = new Date().getTime();

  if (keepAnswers) {
    questionSet.version = qsPayload.version;
    questionSet.timestamp = qsPayload.timestamp;
    questionSet.answersId = state.answersId;
    const { productId, questionSetId, version } = qsPayload;
    delete questionSet.answers.questionSetId;
    setMetaDataInAnswers({ productId, answers: questionSet.answers, metadata: { questionSetId, version, productId } });
  }

  const { modifyAnswers } = getPerQuestionSetConfig({ productId: qsPayload.productId });
  if (modifyAnswers) {
    modifyAnswers({
      answers: questionSet.answers,
      fromQuestionSetId: state.questionSetId,
      fromQuestionSetVersion: state.version,
      productId: qsPayload.productId,
      toQuestionSetId: qsPayload.questionSetId,
      toQuestionSetVersion: qsPayload.version
    });
  }

  return questionSet ? processPrereqsAndCalcs('none', applyChanges(state, questionSet)) : state;
});

processor.add<HpGetAnswersAction>('ANSWERS_RETRIEVED', (state, { payload }) => {
  const questionSet = ensureDefined(payload, 'payload');
  const { answersId, productId } = questionSet;

  let newState = questionSetPayloadMapper({
    currState: state,
    payload: questionSet,
    errorIfNoPayload: true,
    isValidControlType
  }) as HpClientQuestionSetState;

  newState.answersId = ensureDefined(answersId, 'answersId');
  newState.loadTime = new Date().getTime();

  setProductIdCookie(productId);
  setAnswersIdCookie(answersId as string);

  const customExtn = getCustomExtension<HpQuestionSetAnswersRetrievedReducerFunction>(
    'questionSetAnswersRetrievedReducer'
  );
  if (customExtn) {
    newState = customExtn({ prevState: state, state: newState, isDeveloperMode: processor.isDeveloperModeEnabled() });
  }

  newState = processPrereqsAndCalcs('none', newState);

  return newState;
});

processor.add<HpGetQuestionSetsAction>('QUESTIONSETS_RETRIEVED', (currState, action) => {
  const questionSets = action.payload;
  if (lodash.isArray(questionSets)) {
    const summary: HpClientAvailableQuestionSet[] = [];
    questionSets.forEach((qset) => {
      // eslint-disable-next-line prefer-const
      let { friendlyName, productDescription, productId, questionSetId, timestamp } = qset;
      // We should not have duplicate IDs, but if we do...
      if (summary.find((s) => areSame(s.questionSetId, questionSetId))) {
        logWarning(`Remapped duplicated questionSetId "${questionSetId}"`, HpClientDebugFlags.None);
        qset.questionSetId = uuid();
        questionSetId = qset.questionSetId;
      }

      friendlyName = friendlyName || questionSetId;
      const timestampString = timestamp || dateToUtcString(new Date());
      qset.timestamp = timestampString;
      summary.push({
        productId,
        productDescription,
        friendlyName,
        questionSetId,
        timestamp: timestampString,
        questionSet: qset
      });
    });
    const questionPayload = questionSets[0];
    const answersId = uuid();
    setAnswersIdCookie(answersId);
    setProductIdCookie(questionPayload.productId);

    const questionSet = questionSetPayloadMapper({
      currState: { ...currState, answers: {} },
      payload: questionPayload,
      errorIfNoPayload: true,
      isValidControlType
    }) as HpClientQuestionSetState;
    const displayState: HpQuestionDisplayState = questionSet.displayState || {};
    displayState.availableQuestionSets = { items: summary, selected: questionSet.questionSetId };
    questionSet.displayState = displayState;
    questionSet.answersId = answersId;

    const answers: HpAnswerMap = {};
    const { productId, questionSetId, version } = questionSet;
    const metadata: HpMetaData = { productId, questionSetId, version };
    setMetaDataInAnswers({ productId, answers, metadata });

    questionSet.answers = answers;
    return processPrereqsAndCalcs('none', questionSet);
  }

  return currState;
});

processor.add<HpDynamicButtonClickAction>('DYNAMIC_BUTTON_CLICK', (currState, action) => {
  const { payload, type: actionType } = action;
  if (!payload) {
    return currState;
  }

  logInfo(() => {
    const data: Partial<HpDynamicButtonClickPayload> = {};
    lodash.forOwn(payload, (value, key) => {
      // prevent circular reference
      if (key !== 'pageProps') {
        data[key] = value;
      }
    });
    return JSON.stringify({
      type: actionType,
      payload: data
    });
  }, HpClientDebugFlags.ReducerProcessor);

  const { getEvent, groupNumber, indexes, questionId: questionIdOnly } = payload;
  const { type } = getEvent();
  const eventType = type as HpEventTypes;
  const questionId = createControlId({ groupNumber, questionId: questionIdOnly, indexes, options: { noPrefix: true } });
  const question = ensureDefined(currState.questions[questionId], `questions[${questionId}]`);
  const { dependentActions, dependentAnswers } = question.data as HpClientButtonData;
  if (dependentActions) {
    for (const dependentAction of dependentActions) {
      currState = performButtonClickAction(question, groupNumber, currState, dependentAction, indexes);
    }
  }

  if (dependentAnswers) {
    currState = updateAnswerDependentAnswers({
      sourceQuestionId: questionId,
      question,
      state: currState,
      dependentAnswers,
      answerType: 'customer',
      eventType: eventType,
      isChanged: {
        answers: false,
        displayState: false,
        displayStateQuestionsUI: false
      }
    });

    currState = processPrereqsAndCalcs(eventType, currState);
  }

  return currState;
});

processor.add<HpSetActiveSectionAction>('SET_ACTIVE_SECTION', (currState, action) => {
  let state = currState;
  const { payload, type: actionType } = action;
  if (!payload) {
    return state;
  }

  let { activeSectionId: desiredSectionId } = payload;
  if (!desiredSectionId) {
    logError(`The ${actionType} has missing fields in its payload.`);
    return state;
  }

  const { displayState, productId } = state;
  const { activeSectionId: originalSectionId } = displayState;

  // This will determine whether we can go to the desired state.
  // For example, if the customer requested '/residential-details' but cannot actually get to that section (because a previous section's answers are not completed), then
  // the activeSectionid of the 'tmpState' will be the highest available sectionId, which is the section we should move to.
  const tmpState = validateAndMaybeMoveSection({
    buttonAction: 'move-to-section',
    state,
    desiredSectionId,
    options: {
      isValidationRequired: false,
      raiseNavigationChangedEvent: true
    }
  });

  desiredSectionId = tmpState.displayState.activeSectionId;

  const getRouteKeyFromSectionId = getCustomExtension<HpGetRouteKeyFromSectionIdFunction>('getRouteKeyFromSectionId');

  const answersDelta: HpAnswerMap = {};

  if (getRouteKeyFromSectionId) {
    const routeKey = getRouteKeyFromSectionId(desiredSectionId);
    if (routeKey) {
      const showId = getQuestionIdFor(productId, 'showParticularSection');
      answersDelta[showId] = { customer: routeKey, default: null };
    }
  }

  const activeId = getQuestionIdFor(productId, 'activeSectionId');
  answersDelta[activeId] = { customer: desiredSectionId, default: null };

  const { answers } = state;
  state = applyChanges(state, { answers: { ...answers, ...answersDelta } });

  if (desiredSectionId !== originalSectionId) {
    state = navigationChangedAction(state, { activeKey: desiredSectionId }, { isValidationRequired: false });
  }

  return state;
});

const clickActionMap: SMap<HpClientButtonClickActionFunction> = {};

function performButtonClickAction(
  question: HpClientQuestion,
  groupNumber: number | undefined,
  currState: HpClientQuestionSetState,
  dependentAction: HpClientDependentAction,
  indexes?: number[]
): HpClientQuestionSetState {
  const { questionId } = question;
  const { action: buttonAction, condition, props: actionProps = {} } = dependentAction;

  if (condition) {
    if (
      !evaluateCondition({
        condition,
        eventType: 'click',
        groupNumber,
        debugFlag: HpClientDebugFlags.DependentActions,
        questionId,
        questionSet: currState,
        sourceQuestionId: questionId
      })
    ) {
      return currState;
    }
  }

  switch (buttonAction) {
    case 'validate-fields':
    case 'next-section':
    case 'previous-section':
      return validateAndMaybeMoveSection({ state: currState, buttonAction });

    case 'add-control-group':
      return changeState(currState, (state) => {
        const { groupId } = actionProps as HpAddControlGroupActionProps;
        return addControlGroup({ groupId, sourceQuestionId: questionId, state, indexes: indexes });
      });

    case 'remove-control-group':
      return changeState(currState, (state) => {
        const { clearAnswerIdIfNoGroupsRemain } = actionProps as HpRemoveControlGroupActionProps;
        const groupId = question.groupId as string;
        if (groupNumber === undefined) {
          throw new Error(
            `Question ${questionId}: The groupNumber must be provided in the payload for "${buttonAction}".`
          );
        }

        return removeControlGroup({ groupId, clearAnswerIdIfNoGroupsRemain, groupNumber, state, indexes });
      });

    case 'custom':
    case 'submit-form':
      // custom actions should perform an action that calls a dispatcher.
      // they shouldn't do anything here (yet)
      break;

    default:
      {
        const buttonActionFunc = clickActionMap[buttonAction];
        if (buttonActionFunc) {
          return changeState(currState, (state) => {
            return buttonActionFunc({ currState, state, question, props: actionProps as any });
          });
        } else {
          const reason = !buttonAction
            ? 'no action is specified'
            : `the action "${buttonAction}" currently does nothing`;
          logError(`The ${questionId} button was clicked but ${reason}.`);
        }
      }

      break;
  }

  return currState;
}

processor.add<HpFluentAnswerClickAction>('FLUENT_ANSWER_CLICK', (state, action) => {
  if (!action.payload) {
    return state;
  }

  const { fluentAction, targetId: questionId, getEvent } = action.payload;
  const { questions, validationResult } = state;
  let displayState = state.displayState;
  let validation = lodash.cloneDeep(validationResult);
  let isEditing = true;

  if (fluentAction === 'ok') {
    validation = {
      ...validation,
      ...validateFields({
        fields: createValidationModel(state),
        fieldKeysToValidate: [questionId],
        previousResult: lodash.cloneDeep(validation),
        eventType: getEvent().type,
        allowPartialValidation: true
      })
    };
    if (!validation.errors[questionId]) {
      isEditing = false;
    }
  }

  const questionsUI = { ...displayState.questionsUI };
  questionsUI[questionId] = applyChanges(questionsUI[questionId], { isEditing });
  displayState = applyChanges(displayState, { questionsUI });
  return applyChanges(state, { questions, validationResult: validation, displayState });
});

processor.add<HpNavigationChangedAction>('NAVIGATION_CHANGED', (state, action) => {
  return navigationChangedAction(state, action.payload);
});

processor.add<HpHelpClickAction>('HELP_CLICK', (state, action) => {
  if (!action.payload) {
    return state;
  }

  const { question, getEvent } = action.payload;
  const { questionId } = question;
  let displayState = state.displayState;
  const questionsUI = { ...displayState.questionsUI };
  const questionUi = questionsUI[questionId] || {};
  const { helpInfo } = questionUi;
  const { isHelpVisible: wasHelpVisible } = helpInfo || {};
  const isHelpVisible = !wasHelpVisible;

  questionsUI[questionId] = applyChanges(questionUi, { helpInfo: applyChanges(helpInfo, { isHelpVisible }) });
  displayState = applyChanges(displayState, { questionsUI });

  triggerElementToggledEvent({
    getEvent,
    question,
    isVisible: isHelpVisible,
    targetType: 'help'
  });

  return applyChanges(state, { displayState });
});

export interface HpFieldsFailedValidationPayload {
  scrollTo: HpScriptActionTypes;
  sectionIdWithErrors: string;
  validation: HpClientValidationResult;
}

processor.add<HpAction<HpFieldsFailedValidationPayload>>('FIELDS_FAILED_VALIDATION', (state, action) => {
  const { scrollTo, validation } = ensureDefined(action.payload, 'payload');

  const newState: HpClientQuestionSetState = state;

  const partialDisplayState: Partial<HpQuestionDisplayState> = { showValidationSummary: true };
  if (scrollTo === 'scroll-to-question') {
    const questionId = Object.keys(validation.errors)[0];
    partialDisplayState.scriptActions = [{ id: uuid(), questionId, action: scrollTo } as HpQuestionScriptAction];
  } else {
    partialDisplayState.scriptActions = [{ id: uuid(), action: 'scroll-to-top' }];
  }

  const displayState = applyChanges(newState.displayState, partialDisplayState);
  return applyChanges(newState, { validationResult: validation, displayState });
});

processor.add<HpAction<null>>('FIELDS_PASSED_VALIDATION', (state) => {
  const validationResult = { ...state.validationResult, ...createEmptyValidationResult() };
  return applyChanges(state, { validationResult });
});

function navigationChangedAction(
  state: HpClientQuestionSetState,
  payload: { activeKey: string } | undefined,
  options?: HpSetActiveSectionOptions | undefined
): HpClientQuestionSetState {
  if (!payload) {
    return state;
  }

  if (unvalidatedNavigationEnabled && !options) {
    options = {
      isValidationRequired: false,
      bypassMoveToSectionCheck: true
    };
  }

  const { activeKey } = payload;
  let newState = validateAndMaybeMoveSection({
    state,
    buttonAction: 'move-to-section',
    desiredSectionId: activeKey,
    options
  });
  newState = processPrereqsAndCalcs('none', newState);
  return newState;
}

export function questionSetReducer(
  state: HpClientQuestionSetState = getInitialState().questionSet,
  action: HpAction<unknown>
): HpClientQuestionSetState {
  const swappedIn = swapInQuestions(state);
  let newState = processor.process(swappedIn, action);
  if (newState === swappedIn) {
    return state;
  }

  addToWindowRef({ questions: newState.questions }, (win) => win.isSeleniumTest === true);
  newState = swapOutQuestions(newState);
  return newState;
}

export function registerButtonClickAction(
  key: HpControlActionTypes,
  buttonClickAction: HpClientButtonClickActionFunction,
  registerFeature: HpRegisterSomethingFunction | undefined
): void {
  clickActionMap[key] = buttonClickAction;
  if (registerFeature) {
    registerFeature(processor);
  }
}

function dateToUtcString(date: Date): string {
  const pad = (n: number, size?: number): string => lodash.padStart('' + n, size || 2, '0');
  return `${pad(date.getUTCFullYear(), 4)}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}T${pad(
    date.getUTCHours()
  )}:${pad(date.getUTCMinutes())}:${pad(date.getUTCSeconds())}Z`;
}
